import * as next from '../../../types/next'
import type { MultichoiceControlComponentProps } from '../../../utils'
import { CONTROL_NAME } from '../../../utils/blocks/types'
import type { UpBlockHandler } from '../types'

import { upCommonSettings } from './common'

export const up: UpBlockHandler<next.MultipleChoiceBlock> = (uischema) => {
  if (uischema.type !== CONTROL_NAME.MULTICHOICE_CONTROL) {
    throw new Error('Expected a valid Multiple Choice control')
  }

  const castedOptions =
    uischema.options as MultichoiceControlComponentProps<unknown>

  const uiType = upUiType(castedOptions.uiType)
  const choices = upChoices(uiType, castedOptions)
  const maxSelection = castedOptions.maxSelection ?? 'Infinity'
  const buttonType = upLayoutType(castedOptions.buttonType)

  switch (uiType) {
    case next.MULTIPLE_CHOICE_CONTROL_TYPE.CHECKBOX:
      return {
        type: next.BLOCK_TYPE.MULTIPLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          uiType,
          choices,
          maxSelection
        },
        references: {}
      }
    case next.MULTIPLE_CHOICE_CONTROL_TYPE.BUTTON:
      return {
        type: next.BLOCK_TYPE.MULTIPLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          uiType,
          choices,
          maxSelection,
          buttonType
        },
        references: {}
      }

    case next.MULTIPLE_CHOICE_CONTROL_TYPE.IMAGE_BUTTON:
      return {
        type: next.BLOCK_TYPE.MULTIPLE_CHOICE,
        settings: {
          ...upCommonSettings(uischema),
          uiType: next.MULTIPLE_CHOICE_CONTROL_TYPE.IMAGE_BUTTON,
          imageType: upImageType(castedOptions.imageType),
          choices,
          maxSelection
        },
        references: {}
      }
  }
}

const upUiType = (
  uiType: MultichoiceControlComponentProps<unknown>['uiType']
): next.MULTIPLE_CHOICE_CONTROL_TYPE => {
  switch (uiType) {
    case 'checkbox':
      return next.MULTIPLE_CHOICE_CONTROL_TYPE.CHECKBOX

    case 'imageButton':
      return next.MULTIPLE_CHOICE_CONTROL_TYPE.IMAGE_BUTTON

    case 'button':
    default:
      return next.MULTIPLE_CHOICE_CONTROL_TYPE.BUTTON
  }
}

const upLayoutType = (
  buttonType: MultichoiceControlComponentProps<unknown>['buttonType']
): next.BUTTON_CHOICE_LAYOUT_TYPE => {
  switch (buttonType) {
    case 'row':
      return next.BUTTON_CHOICE_LAYOUT_TYPE.ROW

    case 'stack':
      return next.BUTTON_CHOICE_LAYOUT_TYPE.STACK

    case 'chip':
    default:
      return next.BUTTON_CHOICE_LAYOUT_TYPE.CHIP
  }
}

const upImageType = (
  imageType: MultichoiceControlComponentProps<unknown>['imageType']
): next.IMAGE_CHOICE_UI_SUBTYPE => {
  switch (imageType) {
    case undefined:
    case 'infographic':
      return next.IMAGE_CHOICE_UI_SUBTYPE.INFOGRAPHIC

    case 'icon':
      return next.IMAGE_CHOICE_UI_SUBTYPE.ICON

    default:
      throw new Error(`Invalid image type: ${imageType}`)
  }
}

const upChoices = <T extends next.Choice | next.ImageChoice>(
  uiType: next.MULTIPLE_CHOICE_CONTROL_TYPE,
  uischemaOptions: MultichoiceControlComponentProps<unknown>
) => {
  return uischemaOptions.options.reduce<(next.Choice | next.ImageChoice)[]>(
    (acc, choice, i) => {
      acc.push({
        value: choice,
        ...(typeof uischemaOptions.optionsLabels?.[i] !== 'undefined' && {
          label: uischemaOptions.optionsLabels[i]
        }),
        ...(typeof uischemaOptions.optionsIcons?.[i] !== 'undefined' && {
          icon: uischemaOptions.optionsIcons[i]
        }),
        ...(uiType === next.MULTIPLE_CHOICE_CONTROL_TYPE.IMAGE_BUTTON &&
          typeof uischemaOptions.optionsImages?.[i] !== 'undefined' && {
            image: uischemaOptions.optionsImages[i]
          }),
        ...(uiType === next.MULTIPLE_CHOICE_CONTROL_TYPE.IMAGE_BUTTON &&
          typeof uischemaOptions.optionsImageDescriptions?.[i] !==
            'undefined' && {
            imageDescription: uischemaOptions.optionsImageDescriptions[i]
          })
      })

      return acc
    },
    []
  ) as T[]
}
